import firebase from 'firebase/app'
import 'firebase/auth'
import 'firebase/functions'

import { uploadTelemetrySource } from './telemetryPersistence'

// Save and process an iRacing source file. The original remains private in
// Firebase Storage so reports can be rebuilt when the analysis schema changes.
export const processIRacingTelemetry = async (file) => {
    try {
        const user = firebase.auth().currentUser
        if (!user) throw new Error('User not authenticated')
        if (!file) throw new Error('No iRacing telemetry file selected')

        const { telemetryId, storagePath, sourceFile, sourceDigest } = await uploadTelemetrySource({
            file,
            source: 'iracing',
        })

        const functions = firebase.functions()
        if (process.env.NODE_ENV === 'development') {
            functions.useEmulator('localhost', 5001)
        }

        const processTelemetry = functions.httpsCallable('processTelemetry')
        const result = await processTelemetry({
            telemetryId,
            storagePath,
            sourceFile,
            sourceDigest,
        })

        if (!result.data) {
            throw new Error('No data received from iRacing. Please try the import again.')
        }
        if (!result.data.points || !Array.isArray(result.data.points)) {
            throw new Error('No valid points received from iRacing. Please try the import again.')
        }

        return {
            trackInfo: result.data.trackInfo,
            points: result.data.points,
            allLaps: result.data.allLaps.map(lap => ({
                ...lap,
                formattedTime: lap.formattedTime || formatLapTime(lap.lapTime),
                points: lap.points.map(point => ({
                    lat: point.lat,
                    lng: point.lng,
                    alt: point.alt,
                    speed: point.speed,
                    rpm: point.rpm,
                    gear: point.gear,
                    throttle: point.throttle,
                    brake: point.brake,
                    steering: point.steering,
                    latAccel: point.latAccel,
                    longAccel: point.longAccel,
                    yawRate: point.yawRate,
                    engineTemp: point.engineTemp,
                    oilTemp: point.oilTemp,
                    fuelLevel: point.fuelLevel,
                    brakeBias: point.brakeBias,
                    elapsedTime: point.elapsedTime,
                    dist: point.dist,
                    pct: point.pct,
                }))
            })),
            lap: result.data.lap,
            id: result.data.id || telemetryId,
            storagePath,
            sourceFile,
            derivedReport: result.data.derivedReport,
        }
    } catch (error) {
        console.error('Error processing iRacing telemetry:', error)
        throw error
    }
}

export const samplePoints = (points, sampleRate = 10) => points.filter((_, index) => index % sampleRate === 0)

export const validateIRacingData = (points) => {
    if (!Array.isArray(points)) throw new Error('Invalid data format: expected array of points')
    if (points.length === 0) throw new Error('No valid points found in telemetry data')

    points.forEach((point, index) => {
        if (!point.lat || !point.lng) {
            throw new Error(`Invalid point at index ${index}: missing lat/lng`)
        }
        if (typeof point.lat !== 'number' || typeof point.lng !== 'number') {
            throw new Error(`Invalid point at index ${index}: lat/lng must be numbers`)
        }
    })

    return true
}

const formatLapTime = timeInSeconds => {
    if (!timeInSeconds || timeInSeconds <= 0) return null
    const minutes = Math.floor(timeInSeconds / 60)
    const seconds = timeInSeconds % 60
    return minutes > 0
        ? `${minutes}:${seconds.toFixed(3).padStart(6, '0')}`
        : seconds.toFixed(3)
}
